import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { MaskedLines } from "@/components/Reveal";
import PinBadge from "@/components/PinBadge";
import pouches from "@/assets/brand/pouches.png";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative bg-cream border-b border-ink" data-testid="hero">
      <div className="grid lg:grid-cols-2">
        {/* Copy block */}
        <div className="order-2 lg:order-1 flex flex-col justify-center px-4 md:px-10 lg:px-16 py-14 lg:py-0 lg:min-h-[82vh] border-t lg:border-t-0 border-ink">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex self-start kicker bg-lime border border-ink rounded-full px-4 py-1.5"
          >
            {t.hero.badge}
          </motion.span>

          <MaskedLines
            lines={[t.hero.line1, t.hero.line2, t.hero.line3]}
            className="mt-6"
            lineClass="display uppercase text-[19vw] lg:text-[8.6vw] leading-[0.84] tracking-tight text-brand"
          />

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 max-w-md text-base md:text-lg font-medium text-ink/70"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Link to="/shop" data-testid="hero-shop-cta" className="btn-primary h-14 px-8 text-base">
              {t.hero.cta1} <ArrowRight className="w-5 h-5" strokeWidth={2} />
            </Link>
            <Link to="/movement" data-testid="hero-movement-cta" className="btn-ghost h-14 px-8 text-base">
              {t.hero.cta2}
            </Link>
          </motion.div>

          <div className="mt-10 hidden lg:flex items-center gap-2 text-ink/40">
            <ArrowDown className="w-4 h-4" />
            <span className="kicker">{t.hero.scroll}</span>
          </div>
        </div>

        {/* Product block */}
        <div className="order-1 lg:order-2 relative bg-blush lg:border-l border-ink overflow-hidden flex items-center justify-center p-6 md:p-12">
          <motion.div style={{ y: yImg }} className="relative z-10 w-full max-w-xl">
            <motion.img
              src={pouches}
              alt="Anti Unhealthy Club Standbodenbeutel"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="w-full"
            />
            {/* pin sits on the image's top-left corner — never over the brand name */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6, rotate: -14 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 0.8, duration: 0.6, type: "spring", stiffness: 200, damping: 15 }}
              className="absolute -top-4 -left-3 md:-top-6 md:-left-5 z-20"
            >
              <PinBadge size={112} />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
